import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import clsx from "clsx"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { filterApi } from "@/queries/search"
import { toast } from "sonner"
import OperatorsIcon from "@/components/OperatorIcons"
import { Check, Clipboard, Copy, Eye, X } from "lucide-react"
import PrintableTransaction from "@/components/printable-transaction"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { mobileNormalize } from "@/lib/utils"
import NumberWithHoverPoints from "@/components/NumberWithHoverPoint"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  TransactionDetails,
  type Transaction,
  type TransactionStatus,
  type TransactionType,
} from "@/types"
import { Link } from "@tanstack/react-router"
import { DataTable } from "@/components/ui/data-table"
import type { PaginationState } from "@tanstack/react-table"
export const Route = createFileRoute("/client/inquiry/")({
  component: ClientInquiry,
})

interface StatusCell {
  getValue: () => keyof typeof TransactionStatus
  row: any
}

interface TypeCell {
  getValue: () => keyof typeof TransactionType
}

interface DetailCell {
  getValue: () => keyof typeof TransactionDetails
}

function ClientInquiry() {
  const [filtered, setFiltered] = useState<any>()
  const [meta, setMeta] = useState<any>()
  const [showTransaction, setShowTransaction] = useState(false)
  const [mobile, setMobile] = useState("")
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const filterMutation = useMutation({
    mutationFn: (charged_mobile: string) =>
      filterApi(
        "walletTransaction",
        { charged_mobile },
        pagination.pageIndex + 1,
        pagination.pageSize
      ),
    mutationKey: ["filterApi", "inquiry", pagination],
  })

  // Process transactions by order_id: adjust wallet_after_transaction, drop refunds, sort, and return flat list
  const groupTransactionsByOrderId = (
    transactions: Transaction[]
  ): Transaction[] => {
    // 1) Group by order_id (fallback to id if missing)
    const orderGroups = new Map<number | undefined, Transaction[]>()
    transactions.forEach((tx) => {
      const key = tx.order_id ?? tx.id
      if (!orderGroups.has(key)) {
        orderGroups.set(key, [])
      }
      // clone so we don’t mutate originals
      orderGroups.get(key)!.push({ ...tx })
    })

    // 2) Process each group
    const result: Transaction[] = []
    orderGroups.forEach((group) => {
      // a) Find matching refund/purchase pair
      const inc: any = group.find((t) => t.detail === "increase_refund")
      const dec: any = group.find((t) => t.detail === "decrease_purchase_buyer")
      if (inc && dec) {
        dec.wallet_value_after_transaction = inc.wallet_value_after_transaction
      }

      // b) Remove all increase_refund entries
      const filtered = group.filter((t) => t.detail !== "increase_refund")

      // c) Sort so decrease_purchase_buyer comes first
      filtered.sort((a, b) => {
        if (a.detail === "decrease_purchase_buyer") return -1
        if (b.detail === "decrease_purchase_buyer") return 1
        return 0
      })

      // d)end to flat result
      result.push(...filtered)
    })

    return result
  }

  useEffect(() => {
    if (filterMutation.data) {
      setFiltered(
        groupTransactionsByOrderId(
          filterMutation.data.data.data.map((d: any) => ({
            ...d,
            extra_info:
              typeof d.extra_info === "string"
                ? JSON.parse(d.extra_info)
                : d.extra_info,
          }))
        )
      )
      setMeta(filterMutation.data.data.meta)
    }
  }, [filterMutation.data])

  useEffect(() => {
    if (mobile) {
      handleReport()
    }
  }, [pagination])

  const handleReport = () => {
    filterMutation.mutate(mobileNormalize(mobile))
  }

  const columns: any = [
    {
      header: "اپراتور",
      accessorKey: "extra_info",
      id: "operator",
      cell: ({ getValue }: any) => {
        return (
          getValue()?.operator && (
            <>
              <OperatorsIcon
                operator={getValue()?.operator?.name.toLowerCase()}
                operatorColors
              />
            </>
          )
        )
      },
    },
    {
      header: "شماره سند",
      accessorKey: "resnumber",
      cell: ({ row }: any) => {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="p-2">
                <Eye />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="flex items-center">
              {row.original.resnumber}
              <Button
                variant="link"
                onClick={() => {
                  if ("clipboard" in navigator) {
                    navigator.clipboard.writeText(row.original.resnumber)
                    toast.success("کپی شد")
                  }
                }}
              >
                <Copy />
              </Button>
            </PopoverContent>
          </Popover>
        )
      },
    },
    {
      header: "شماره موبایل",
      accessorKey: "charged_mobile",
      cell: ({ getValue }: any) => {
        return getValue() ? <>{getValue()?.replace("98", "0") || "--"}</> : "-"
      },
    },
    {
      header: "نام مخاطب",
      accessorKey: "extra_info",
      id: "phone_book_name",
      cell: ({ getValue }: any) => {
        return getValue()?.phone_book_name || "-"
      },
    },
    {
      header: "محصول",
      accessorKey: "product_name",
      id: "product",
      cell: ({ getValue, row }: any) => {
        if (row.original.product_type === 'td_lte_internet') {
          return `${getValue()}`.replace('- دائمی', '')
        }
        return <>{getValue()}</>
      },
    },
    {
      header: "قیمت محصول (ریال)",
      accessorKey: "extra_info",
      id: "price",
      cell: ({ getValue }: any) => {
        return getValue() ? (
          <>
            <NumberWithHoverPoints number={getValue()?.price} />
          </>
        ) : (
          "-"
        )
      },
    },
    {
      header: "مقدار (ریال)",
      accessorKey: "value",
      cell: ({ getValue }: any) => {
        return <NumberWithHoverPoints number={getValue()} />
      },
    },
    {
      header: "مقدار بعد تراکنش (ریال)",
      accessorKey: "wallet_value_after_transaction",
      cell: ({ getValue }: any) => {
        return <NumberWithHoverPoints number={getValue()} />
      },
    },
    {
      header: "وضعیت",
      accessorKey: "status",
      cell: ({ getValue, row }: StatusCell) => {
        let status = getValue() === "confirmed"
        if (row.original.third_party_status !== null) {
          if (row.original.third_party_status !== 1) {
            status = false
          }
        }
        const icon = status ? <Check /> : <X />
        const textColor = status ? "text-green-500" : "text-red-500"

        return (
          <div
            className={clsx("d-flex flex-nowrap align-items-center", textColor)}
          >
            {icon}
            {status ? "موفق" : "ناموفق"}
          </div>
        )
      },
    },
    // {
    //   header: 'نوع',
    //   accessorKey: 'type',
    //   cell: ({getValue}: TypeCell) => {
    //     const icon = getValue() === 'increase' ? 'arrow-up' : 'arrow-down'
    //     const textColor = getValue() === 'increase' ? 'text-green-500' : 'text-red-500'
    //     return (
    //       <div className={clsx('d-flex align-items-center', textColor)}>
    //         {TransactionType[getValue()]}
    //         <KTIcon iconName={icon} className='fs-2 me-1' />
    //       </div>
    //     )
    //   },
    // },
    {
      header: "جزئیات",
      accessorKey: "detail",
      cell: ({ getValue }: DetailCell) => {
        return <>{TransactionDetails[getValue()]}</>
      },
    },
    {
      header: "IP",
      accessorKey: "extra_info",
      id: "ip",
      cell: ({ row }: any) => {
        console.log(row.original.extra_info)
        return row.original.extra_info?.ip || "-"
      },
    },
    {
      header: "تاریخ",
      accessorKey: "created_at",
      cell: ({ getValue }: { getValue: () => Date }) => {
        return (
          <span dir="ltr">
            {Intl.DateTimeFormat("fa-ir", {
              timeStyle: "short",
              dateStyle: "short",
            }).format(new Date(getValue()))}
          </span>
        )
      },
    },

    {
      id: "print",
      cell: ({ row }: any) => {
        return (
          <>
            <PrintableTransaction transaction={row.original} />
          </>
        )
      },
    },
  ]

  const clear = () => {
    setFiltered([])
    setMobile("")
  }

  const isDigitsOnly = (text: string) => {
    // الگوی منظم برای تطبیق اعداد انگلیسی، فارسی و عربی
    const digitPattern = /^[0-9۰-۹٠-٩]*$/
    return digitPattern.test(text)
  }

  const handlePaste = () => {
    navigator.clipboard.readText().then((text) => {
      if (isDigitsOnly(text)) {
        setMobile(text)
      }
    })
  }

  return (
    <>
      <Dialog
        open={showTransaction}
        onOpenChange={(o) => setShowTransaction(o)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>جزئیات تراکنش</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Card className="">
        <div className="flex justify-between items-center">
          <CardHeader>
            <CardTitle>استعلام شماره</CardTitle>
            <CardDescription>
              در اینجا شما می توانید تراکنش های یک شماره خاص را بگیرید
            </CardDescription>
          </CardHeader>
          <Link to="/">
            <Button className="m-4">بازگشت</Button>
          </Link>
        </div>

        <CardContent>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              handleReport()
            }}
          >
            <div className="flex flex-col gap-2">
              <Label>شماره موبایل</Label>
              <div className="relative">
                <Input
                  phone
                  value={mobile}
                  dir="ltr"
                  maxLength={11}
                  onChange={(e) => setMobile(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-2 top-2"
                  onClick={handlePaste}
                >
                  <Clipboard />
                </button>
              </div>
            </div>
            <Button
              disabled={mobile === ""}
              type="submit"
              onClick={handleReport}
              isLoading={filterMutation.isPending}
            >
              گزارش
            </Button>
            {filtered?.length > 0 && (
              <Button variant="outline" onClick={clear} type="button">
                پاک کردن
              </Button>
            )}
          </form>
          {filtered?.length > 0 && (
            <DataTable
              columns={columns}
              data={filtered}
              pageCount={meta?.last_page}
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          )}
        </CardContent>
      </Card>
    </>
  )
}
